import type { AgentModeWritableKey } from '@developerz/domain';
import { createMemo, createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { AgentModeView } from '../lib/agent-modes';
import type { RouteRefusal } from '../lib/route-refusal';
import { ConfirmDialog } from './ConfirmDialog';
import { RouteRefusalNotice } from './RouteRefusalNotice';
import { Switch } from './Switch';

// The mode switch on a mode-backed card (#5211 AC5, epic #5200) — worker on
// Coder, ci_fixer on CI fixer, devops, scout, reviewer: one switch per agent, each
// editing its own `agent_modes` row. CI fixer has had its own `ci_fixer` switch
// since #7390, which reverses #5200 G7 ("One `worker` switch, on the Coder card,
// labelled 'Coder + CI fixer'") under the #6552 v2 owner ruling (ivndev001): every
// machine-level switch is per role, so CI Fixer's must first be separable from
// Coder's. A `ConfirmDialog` names what pauses (`agent_modes.holds.<mode>`, already
// the per-mode sentence `AgentModesPanel` shows under each row — reused verbatim,
// not re-authored) before the write. The switch is the shared `Switch` (#5625): a
// `role="switch"` button whose visible On / Off sits beside it.
//
// RE-READ, NO OPTIMISTIC PAINT: an interactive switch shows `view.enabled` as
// read, never a click's guess — `agent-mode-save.ts`'s design decision.
//
// TWO DATA SOURCES, per AC8 / spec-gate note 1. `view` (`GET /v1/agent-modes`)
// needs `write:prompts`; a member/viewer never gets one, so it is always
// undefined for them. Rather than show NOTHING — a disabled switch standing in
// for a HIDDEN one is what note 1 actually forbids for the drawer's TOOLS
// section, but AC8 still requires "disabled controls with Ask an admin" for
// the switch itself — this falls back to `activityEnabled`
// (`AgentActivityWire`'s own `mode_enabled`, read with the same `read:tasks`
// every card already holds) for a READ-ONLY rendering: the flag, disabled,
// "Ask an admin", no `ConfirmDialog` (there is nothing this caller could
// confirm). Both sources absent (still loading, or a null-shaped read) renders
// nothing, same honesty rule as everywhere else on this page.

// Keyed on the keys the ORG scope can write (#7008): the card's switch edits the org row, so a
// stored key no org door may set has no switch and no label.
const SWITCH_LABEL_KEY: Readonly<Record<AgentModeWritableKey<'org'>, string>> = {
  worker: 'agents.mode_switch.label.worker',
  devops: 'agents.mode_switch.label.devops',
  scout: 'agents.mode_switch.label.scout',
  reviewer: 'agents.mode_switch.label.reviewer',
  ci_fixer: 'agents.mode_switch.label.ci_fixer',
  planner: 'agents.mode_switch.label.planner',
};

/** The visible state, from the card's own On / Off keys. */
const stateText = (on: boolean): string =>
  on ? t('agents.mode_switch.on') : t('agents.mode_switch.off');

export type AgentModeSwitchProps = {
  readonly modeKey: AgentModeWritableKey<'org'>;
  /** Undefined = not loaded yet, or this caller cannot read modes (`write:prompts`). */
  readonly view: AgentModeView | undefined;
  /** The `read:tasks` fallback (`AgentActivitySlice.mode_enabled`) — the one
   * mode-state read that reaches every role. Used ONLY when `view` is absent. */
  readonly activityEnabled: boolean | undefined;
  readonly canEdit: boolean;
  readonly busy: boolean;
  readonly onConfirm: (enabled: boolean) => void;
  /** A refused write for THIS mode — a 409 `config-stale` names who saved
   * first (AC2); never swallowed. `null` when nothing is standing. */
  readonly refusal: RouteRefusal | null;
  readonly onDismissRefusal: () => void;
};

export function AgentModeSwitch(props: AgentModeSwitchProps): JSX.Element {
  const [confirming, setConfirming] = createSignal(false);
  /** `view.enabled` when the caller holds `write:prompts`; else the
   * `read:tasks` fallback. `undefined` only when NEITHER source has landed. */
  const enabled = createMemo<boolean | undefined>(
    () => props.view?.enabled ?? props.activityEnabled,
  );

  return (
    <Show when={enabled() !== undefined}>
      <Show
        when={props.view}
        fallback={
          // `view` absent for an EDITOR too (still loading, or the GET
          // simply hasn't landed yet) — "Ask an admin" only when this
          // caller genuinely lacks the scope; an admin mid-load sees
          // nothing rather than a wrong, momentary lock message.
          <Show when={!props.canEdit}>
            <p class="agent-card__mode-readonly">
              <span>{stateText(enabled() === true)}</span>
              {' · '}
              <span class="agent-card__mode-locked">{t('agents.picker.locked')}</span>
            </p>
          </Show>
        }
      >
        {(view) => (
          <>
            <div class="agent-card__mode-switch">
              <Switch
                checked={view().enabled}
                disabled={!props.canEdit || props.busy}
                // Controlled: repainted from the next read, never the click.
                onActivate={() => setConfirming(true)}
                stateLabel={stateText(view().enabled)}
                aria-label={t(SWITCH_LABEL_KEY[props.modeKey])}
              />
            </div>
            <RouteRefusalNotice refusal={props.refusal} onDismiss={props.onDismissRefusal} />
            <ConfirmDialog
              open={confirming()}
              title={
                view().enabled
                  ? t('agents.mode_switch.pause_title', {
                      name: t(SWITCH_LABEL_KEY[props.modeKey]),
                    })
                  : t('agents.mode_switch.resume_title', {
                      name: t(SWITCH_LABEL_KEY[props.modeKey]),
                    })
              }
              desc={t(`agent_modes.holds.${props.modeKey}`)}
              cancelLabel={t('agents.mode_switch.cancel')}
              actionLabel={
                view().enabled ? t('agents.mode_switch.pause') : t('agents.mode_switch.resume')
              }
              destructive={view().enabled}
              onCancel={() => setConfirming(false)}
              onConfirm={() => {
                setConfirming(false);
                props.onConfirm(!view().enabled);
              }}
            />
          </>
        )}
      </Show>
    </Show>
  );
}
